<script setup>
  import {
    SidebarMenuSub,
    SidebarMenuSubItem,
    SidebarMenuSubButton,
    SidebarContent,
    Sidebar,
    SidebarGroup,
    SidebarGroupContent,
    SidebarGroupLabel,
    SidebarTrigger,
    SidebarMenu,
  } from "@/components/ui/sidebar/index.ts";

  import { ArrowLeft, ArrowUpRight, ChevronsUpDown, ChevronDown, Users, BookOpen, KeyRound, LogOut } from "lucide-vue-next";
  import { Badge } from "@/components/ui/badge";
  import { Collapsible, CollapsibleTrigger, CollapsibleContent } from "@/components/ui/collapsible/index.ts";
  import SidebarMenuItem from "@/components/ui/sidebar/SidebarMenuItem.vue";
  import SidebarMenuButton from "@/components/ui/sidebar/SidebarMenuButton.vue";
  import { useSidebar } from "@/components/ui/sidebar/utils.ts";
  import SidebarFooter from "@/components/ui/sidebar/SidebarFooter.vue";
  import ContractSidebarWidget from "@/components/Contracts/ContractSidebarWidget.vue";
  import AnnouncementSidebarWidget from "@/components/Announcements/AnnouncementSidebarWidget.vue";
  import { ref } from "vue";
  import { captureEvent, resetAnalytics } from "@/lib/analytics";
  import {
    DropdownMenu,
    DropdownMenuTrigger,
    DropdownMenuContent,
    DropdownMenuSeparator,
    DropdownMenuGroup,
    DropdownMenuItem,
  } from "@/components/ui/dropdown-menu/index.ts";
  import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar/index.ts";
  import { storeToRefs } from "pinia";
  import { useCurrentUserStore } from "@stores/currentUser.js";
  import { useUser } from "@composables/useUser";
  import LogoutConfirmAlert from "@/components/App/Sidebar/LogoutConfirmAlert.vue";
  import useMyFetch from "@stores/useMyFetch.js";
  import { userType } from "@utils/constants/userRoles";
  import { computed } from "vue";

  // Logo imports
  import logo from "@/assets/images/logo_mini.svg";
  import logoDev from "@/assets/images/logo_mini_dev.svg";
  import logoStaging from "@/assets/images/logo_mini_staging.svg";

  const props = defineProps({
    navigation: {
      type: Array,
      required: true,
      default: () => [],
    },
    back: {
      type: Object,
      default: null,
    },
  });

  const { user, isLoggedIn, canManageMembers } = storeToRefs(useCurrentUserStore());
  const { getInitials } = useUser();
  const { open, toggleSidebar } = useSidebar();

  const isClient = computed(() => user.value?.user_type === userType.CLIENT);

  // Determine logo based on hostname
  const logoSrc = computed(() => {
    const hostname = window.location.hostname;
    if (hostname.endsWith(".pr.episto.fr")) return logoStaging;

    const logos = { localhost: logoDev, "staging-gcp.episto.fr": logoStaging, "app.episto.fr": logo };

    return logos[hostname] || logo;
  });

  const handleSidebarClick = (event) => {
    // Only toggle if sidebar is collapsed and click is not on an interactive element
    if (!open.value && !event.target.closest('a, button, [role="button"]')) {
      toggleSidebar();
    }
  };

  const items = [
    {
      title: "Documentation",
      url: "https://episto.slite.page/p/YlqURoH5cKfESi/Episto-Help-Center",
      icon: BookOpen,
    },
  ];

  const showLogoutDialog = ref(false);

  const handleLog = () => (isLoggedIn.value ? (showLogoutDialog.value = true) : handleLogIn());
  const handleLogIn = () => window.location.assign(`${window.location.origin}/login`);
  async function handleLogOut() {
    await useMyFetch(`${window.location.origin}/users/sign_out`).delete();

    captureEvent("logged_out");

    resetAnalytics();
    window.location.assign(`${window.location.origin}/login`);
  }

  const hasChildrenActive = (item) => {
    return item.children?.some((child) => child.isActive);
  };

  const openItems = ref({});

  const initializeOpenStates = () => {
    const initialState = {};
    props.navigation.forEach((section) => {
      section.items.forEach((item) => {
        if (item.children) {
          initialState[item.title] = hasChildrenActive(item);
        }
      });
    });
    openItems.value = initialState;
  };

  initializeOpenStates();

  const handleCollapsibleToggle = (itemTitle, newState) => {
    if (newState) {
      // Close all other items when opening this one
      const newOpenState = {};
      Object.keys(openItems.value).forEach((key) => {
        newOpenState[key] = key === itemTitle;
      });
      openItems.value = newOpenState;
    } else {
      openItems.value = { ...openItems.value, [itemTitle]: false };
    }
  };

  // Get the open state for an item
  const getOpenState = (itemTitle) => {
    return openItems.value[itemTitle] || false;
  };
</script>

<template>
  <Sidebar
    collapsible="icon"
    @click="handleSidebarClick">
    <SidebarContent>
      <SidebarGroup v-if="open">
        <div class="flex justify-between pt-3 pb-1">
          <RouterLink to="/">
            <SidebarGroupLabel>
              <img
                :src="logoSrc"
                class="h-6"
                alt="Episto Logo"
                title="Episto Logo" />
            </SidebarGroupLabel>
          </RouterLink>
          <SidebarTrigger v-if="open" />
        </div>
      </SidebarGroup>

      <SidebarGroup
        v-if="!open"
        class="pt-3">
        <SidebarTrigger />
      </SidebarGroup>

      <SidebarGroup
        v-if="props.back"
        class="pt-0">
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              as-child
              :tooltip="`Back to ${props.back.label}`"
              class="text-sidebar-muted hover:text-sidebar-foreground">
              <RouterLink
                :to="props.back.to"
                data-cy="sidebar-back">
                <ArrowLeft />
                <span>{{ props.back.label }}</span>
              </RouterLink>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarGroup>

      <template v-for="section in props.navigation">
        <SidebarGroup
          v-if="section.items.some((item) => item.if)"
          :key="section.title">
          <SidebarGroupLabel v-if="open && section.title">{{ section.title }}</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              <template v-for="item in section.items">
                <SidebarMenuItem
                  v-if="item.if"
                  :key="item.title">
                  <SidebarMenuButton
                    v-if="!item.children"
                    as-child
                    :is-active="item.isActive"
                    :tooltip="item.title">
                    <RouterLink
                      :to="item.url"
                      class="link">
                      <div class="flex items-center">
                        <component
:is="item.icon"
                          :size="16"
                          :stroke-width="1.8"
                          class="mr-2.5 size-4 flex-shrink-0" />
                        <span v-if="open">{{ item.title }}</span>
                        <Badge
                          v-if="open && item.badge"
                          :variant="item.badgeVariant"
                          class="ml-2 rounded px-1.5 text-3xs">
                          {{ item.badge }}
                        </Badge>
                      </div>
                    </RouterLink>
                  </SidebarMenuButton>

                  <template v-else>
                    <Collapsible
                      :key="item.title"
                      as-child
                      class="group/collapsible"
                      :open="getOpenState(item.title)"
                      @update:open="(newState) => handleCollapsibleToggle(item.title, newState)">
                      <SidebarMenuItem>
                        <CollapsibleTrigger as-child>
                          <SidebarMenuButton
                            :tooltip="item.title"
                            :is-active="hasChildrenActive(item)">
                            <div
                              class="flex items-center"
                              @click="open = true">
                              <component
:is="item.icon"
                                :size="16"
                                :stroke-width="1.8"
                                class="mr-2.5 size-4 flex-shrink-0" /><span v-if="open">{{ item.title }}</span>
                            </div>
                            <ChevronDown
                              class="ml-auto transition-transform duration-200 group-data-[state=closed]/collapsible:-rotate-90" />
                          </SidebarMenuButton>
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                          <SidebarMenuSub>
                            <template v-for="child in item.children">
                              <SidebarMenuSubItem
                                v-if="child.if ?? true"
                                :key="child.title">
                                <SidebarMenuSubButton
                                  as-child
                                  :is-active="child.isActive">
                                  <RouterLink :to="child.url">
                                    <span>{{ child.title }}</span>
                                  </RouterLink>
                                </SidebarMenuSubButton>
                              </SidebarMenuSubItem>
                            </template>
                          </SidebarMenuSub>
                        </CollapsibleContent>
                      </SidebarMenuItem>
                    </Collapsible>
                  </template>
                </SidebarMenuItem>
              </template>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </template>
    </SidebarContent>

    <AnnouncementSidebarWidget v-if="open" />
    <ContractSidebarWidget v-if="open" />

    <SidebarFooter class="px-3">
      <div class="mx-2 h-px bg-sidebar-border/60" />

      <SidebarMenu>
        <SidebarMenuItem
          v-for="item in items"
          :key="item.title">
          <SidebarMenuButton
            as-child
            :is-active="item.isActive">
            <a
              :href="item.url"
              target="_blank"
              rel="noopener noreferrer">
              <div class="flex items-center">
                <component
:is="item.icon"
                  :size="16"
                  :stroke-width="1.8"
                  class="mr-2.5 size-4 flex-shrink-0" />{{ item.title }}
              </div>
              <ArrowUpRight
                v-if="open"
                :stroke-width="1.8"
                class="ml-auto size-3.5" />
            </a>
          </SidebarMenuButton>
        </SidebarMenuItem>
      </SidebarMenu>

      <SidebarMenu class="mt-2">
        <SidebarMenuItem>
          <DropdownMenu>
            <DropdownMenuTrigger
              as-child
              class="cursor-pointer">
              <SidebarMenuButton
                size="lg"
                :class="[
                  'data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-foreground',
                  !open ? 'justify-center' : '',
                ]">
                <Avatar :class="['size-7', open && '-ml-1.5']">
                  <AvatarImage
                    src=""
                    alt="" />
                  <AvatarFallback
                    class="flex items-center justify-center rounded-full bg-sidebar-border text-2xs font-semibold text-sidebar-foreground">
                    {{ getInitials }}
                  </AvatarFallback>
                </Avatar>
                <div
                  v-if="open"
                  class="grid flex-1 text-left text-sm leading-tight">
                  <span class="truncate text-13 font-medium text-sidebar-foreground">{{ user?.first_name }} {{ user?.last_name }}</span>
                  <span class="truncate text-xs font-light text-sidebar-muted">{{ user?.email }}</span>
                </div>
                <ChevronsUpDown
                  v-if="open"
                  class="ml-auto size-4" />
              </SidebarMenuButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              class="w-[--reka-dropdown-menu-trigger-width] min-w-56 rounded-lg z-100"
              side="right"
              align="end"
              :side-offset="4">
              <DropdownMenuGroup v-if="user?.default_account_name">
                <DropdownMenuItem class="text-foreground-subtle hover:text-foreground-subtle">
                  {{ user.default_account_name }}
                </DropdownMenuItem>
              </DropdownMenuGroup>
              <DropdownMenuSeparator v-if="user?.default_account_name" />
              <DropdownMenuGroup>
                <DropdownMenuItem>
                  <a
                    href="/users/edit"
                    class="flex items-center"
                    ><KeyRound
  :stroke-width="1.5"
  class="size-3.5 mr-2" />Reset Password</a
                  >
                </DropdownMenuItem>
                <DropdownMenuItem v-if="isClient && canManageMembers">
                  <RouterLink
                    to="/members"
                    class="flex items-center"
                    ><Users
  :stroke-width="1.5"
  class="size-3.5 mr-2" />Manage members</RouterLink
                  >
                </DropdownMenuItem>
                <DropdownMenuItem
                  class="cursor-pointer flex items-center"
                  @click="handleLog"
                  ><LogOut
  :stroke-width="1.5"
  class="size-3.5 mr-2" />Log out</DropdownMenuItem
                >
              </DropdownMenuGroup>
            </DropdownMenuContent>
          </DropdownMenu>
        </SidebarMenuItem>
      </SidebarMenu>
    </SidebarFooter>
  </Sidebar>

  <LogoutConfirmAlert
    :open="showLogoutDialog"
    @update:open="showLogoutDialog = $event"
    @confirm="handleLogOut" />
</template>
